/**
 * Helpers for reading planning sections from planning templates.
 *
 * Each PlanningTemplate now owns its own sections + steps with editable
 * labels and colors. There is no org-level structure.
 *
 * Components that need to render a planning grid read sections from the
 * template the planning is tied to. Components that need to show "all
 * possible sections" (e.g. the charge artisan picker, the partner
 * assignment UI) aggregate across all templates.
 */

import type {
  PlanningSection,
  PlanningStep,
  PlanningTemplate,
  PlanningTemplateSection,
  PlanningTemplateStep,
} from "types";

/** Legacy color fallbacks — used when a section has no explicit color. */
export const FALLBACK_CELL_COLOR = "#262626";
export const FALLBACK_HEADER_BG = "#E5E7EB";
export const FALLBACK_HEADER_TEXT = "#374151";

/**
 * Fallback label builder when a section/step has no explicit label.
 * Turns `my_section_key` → `My section key`.
 */
export function prettifyKey(key: string | undefined | null): string {
  if (!key) return "";
  return key.replace(/_/g, " ").trim().replace(/^./, (c) => c.toUpperCase());
}

/**
 * Read active (non-deleted) sections from a template, sorted by `order`.
 * Steps within each section are also filtered and sorted.
 */
export function getTemplateSections(
  template: PlanningTemplate | null | undefined,
): PlanningTemplateSection[] {
  const all = template?.sections || [];
  return all
    .filter((s) => !s.deleted_at)
    .map((s) => ({
      ...s,
      steps: (s.steps || [])
        .filter((st) => !st.deleted_at)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Aggregate unique sections across multiple templates.
 * Dedupes by `section_key`. On conflict (same key, different label), the
 * first template to declare it wins.
 * Used by the charge artisan picker and the partner assignment UI, where
 * the user wants "all sections the org is using".
 */
export function aggregateSectionsAcrossTemplates(
  templates: PlanningTemplate[],
): PlanningTemplateSection[] {
  const map = new Map<string, PlanningTemplateSection>();
  for (const tpl of templates) {
    for (const section of getTemplateSections(tpl)) {
      if (!map.has(section.section_key)) {
        map.set(section.section_key, section);
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/**
 * Resolve section label from the template, or fallback to a prettified key.
 */
export function sectionLabel(section: PlanningTemplateSection | undefined, fallbackKey?: string): string {
  return section?.label || prettifyKey(section?.section_key || fallbackKey);
}

/**
 * Resolve step label from the template, or fallback to a prettified key.
 */
export function stepLabel(step: PlanningTemplateStep | undefined, fallbackKey?: string): string {
  return step?.label || prettifyKey(step?.step_key || fallbackKey);
}

/** Resolve cell color, with fallback. */
export function cellColor(section: PlanningTemplateSection): string {
  return section.cell_color || FALLBACK_CELL_COLOR;
}

export function headerBg(section: PlanningTemplateSection): string {
  return section.header_bg || FALLBACK_HEADER_BG;
}

export function headerText(section: PlanningTemplateSection): string {
  return section.header_text || FALLBACK_HEADER_TEXT;
}

/** Find a section by key in a template. */
export function findSectionInTemplate(
  template: PlanningTemplate | null | undefined,
  sectionKey: string,
): PlanningTemplateSection | undefined {
  return getTemplateSections(template).find((s) => s.section_key === sectionKey);
}

/** Find a step by key in a section. */
export function findStepInSection(
  section: PlanningTemplateSection | undefined,
  stepKey: string,
): PlanningTemplateStep | undefined {
  return section?.steps.find((st) => st.step_key === stepKey);
}

/**
 * A planning section "view" with all display fields resolved.
 * The label/colors come from the planning's snapshot first, then fall back
 * to the template (for older docs that pre-date the snapshot), then to the
 * prettified key / fallback colors.
 */
export interface ResolvedPlanningSection {
  section_key: string;
  label: string;
  cell_color: string;
  header_bg: string;
  header_text: string;
  steps: ResolvedPlanningStep[];
}

export interface ResolvedPlanningStep {
  step_key: string;
  label: string;
}

/** Resolve a planning section's display fields, with template fallback. */
export function resolveSectionFromPlanning(
  planningSection: PlanningSection,
  template: PlanningTemplate | null | undefined,
): ResolvedPlanningSection {
  const tplSection = template?.sections?.find(
    (s) => s.section_key === planningSection.section_key && !s.deleted_at,
  );

  return {
    section_key: planningSection.section_key,
    label:
      planningSection.label ||
      tplSection?.label ||
      prettifyKey(planningSection.section_key),
    cell_color: tplSection?.cell_color || FALLBACK_CELL_COLOR,
    header_bg: tplSection?.header_bg || FALLBACK_HEADER_BG,
    header_text: tplSection?.header_text || FALLBACK_HEADER_TEXT,
    steps: planningSection.steps.map((planningStep) => {
      const tplStep = tplSection?.steps?.find(
        (st) => st.step_key === planningStep.step_key && !st.deleted_at,
      );
      return {
        step_key: planningStep.step_key,
        label:
          planningStep.label ||
          tplStep?.label ||
          prettifyKey(planningStep.step_key),
      };
    }),
  };
}
